<template>
  <div class="site site-full site-editor max-h-screen h-screen overflow-y-auto basis-0 bg-gray-100 dark:bg-gray-800">
    <div :class="{'esign-right-bar-enabled': rightSidebarOpen}" class="site_wrap">
      <!-- Toolbar -->
      <TopToolbar
        :document-name="documentFileName"
        :is-dirty="isDirty"
        @saveTemplate="saveTemplateSafely"
        @toolbarHeight="coordinateData.topBarHeight = $event"
        @open-shortcuts="shortcutsDialog = true"
      />

      <!-- Unsaved draft restore banner -->
      <div v-if="restorableDraft" class="fw-draft-banner">
        <v-card class="pa-3" elevation="8">
          <div class="flex items-center gap-3">
            <v-icon color="primary">mdi-history</v-icon>
            <div>
              <div class="font-medium">Unsaved draft found</div>
              <div class="text-sm text-medium-emphasis">Autosaved {{ restorableDraftLabel }} on this device.</div>
            </div>
            <v-btn size="small" variant="text" @click="discardDraft">Discard</v-btn>
            <v-btn color="primary" size="small" variant="tonal" @click="restoreDraft">Restore</v-btn>
          </div>
        </v-card>
      </div>

      <!-- Content -->
      <div class="site_content">
        <div class="content_wrap h-full max-w-none relative">
          <!-- Left sidebar-->
          <LeftSidebar @addField="addField($event)" @clear-all-fields="removeAllFieldsDialog = true"/>

          <!-- Main Content -->
          <div id="esign-editor-main-content" ref="content_main" class="content_main flex overflow-scroll" @contextmenu="onStageContextMenu" @mousemove="checkDroppable($event)" @scroll="handleScroll">
            <div id="standard-documents" class="standardDocuments" style="flex-grow: 1;">
              <DocumentStage
                :content-main-height="contentMainHeight"
                :content-main-width="contentMainWidth"
                :determine-display-font-size="determineDisplayFontSize"
                :determine-text-lines="determineTextLines"
                :determine-y-text-offset="determineYTextOffset"
                :doc-image="docImage"
                :document-file-name="documentFileName"
                :element-class-object="elementClassObject"
                :element-fill-color="elementFillColor"
                :fields="stageDataForRender"
                :left-and-right-document-margin="LeftAndRightDocumentMargin"
                :local-document-images="localDocumentImages"
                :page-view-rectangle="pageViewRectangle"
                :snap-guides="activeSnapGuides"
                :tippy-content="tippyContent"
                @check-coords="checkCoords"
                @doc-view-load="onDocViewLoad"
                @img-load="onImgLoad"
                @select-element="selectElement"
                @signer-clicked="signerElementClicked"
                @svg-clicked="svgClicked"
              >
                <template #drag-handles>
                  <FieldDragHandles
                    v-if="currentWorkingDataElement"
                    :element="currentWorkingDataElement"
                    :handles-on="dragHandlesOn"
                    @resize="handleResize"
                  />
                </template>
              </DocumentStage>

              <div ref="dataEditor" style=" width: 100px; height: 25px; position: absolute; transform: scale(1); transform-origin: left top;">
                <div class="tab-editor-interface_content">
                  <textarea v-model="editorText" :readonly="currentWorkingDataElement?.readOnly" maxlength="4000" tabindex="-1"></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Sidebar -->
          <RightSideColumn
            :current-page="currentViewPage"
            :document-file-name="documentFileName"
            :document-images="localDocumentImages"
            @scroll-to-page="scrollToPage"
          />

        </div>
      </div>

      <TemplateRightSidebarV2
        @update:esign-field-deleted="dragHandlesOn = false; removeSelectedItems();"

      />
    </div>

    <div v-if="debugDrawerOpen" class="absolute top-o">
      {{ coordinateData }}
    </div>

    <div v-if="debugDrawerOpen" class="z-10 absolute top-20 p-1 debug debug-draggable w-52" style="background-color: #0acf97">
      <div class="flex flex-col">
        <small>DEBUG BUTTONS</small>
        <v-btn class="mt-1" size="x-small" @click="adjustDataCoords()">
          adjustDataCoords
        </v-btn>

        <v-btn class="mt-1" color="error" size="x-small" @click="toggleDebugDrawer()">
          TURN OFF DEBUG.
        </v-btn>

        {{ selectedFieldV3Coordinates }}

        {{ docImage }} <br>

        {{ contentLeftSidebarWidth }}<br>

        {{ LeftAndRightDocumentMargin }} <br>
        {{ scrollTop }} <br>

      </div>
    </div>

    <div v-if="debugDrawerOpen" class="z-10 border debug debug-draggable absolute overflow-hidden h-[600px] w-[300px] max-w-[300px] bg-black/90 bottom-3 border-gray-500" style="left: 0;">
      <div>
        <div class="text-white small fixed debug-draggable__header">The First Staged Data item</div>
        <code>
          <pre v-html="formatJson(firstStagedData)"/>
        </code>
      </div>
    </div>

    <v-dialog v-model="removeAllFieldsDialog" max-width="400">
      <v-card>
        <v-card-title>Are you sure?</v-card-title>

        <v-card-text>
          You are about to delete all the fields on this document and start over.
        </v-card-text>

        <v-card-actions>
          <v-spacer/>
          <v-btn
            variant="text"
            @click="removeAllFieldsDialog = false"
          >
            {{ $t('nevermind') }}
          </v-btn>
          <v-btn color="error" @click="removeAllFields()">
            Reset Fields
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="leaveGuardDialog" max-width="440" persistent>
      <v-card>
        <v-card-title>Unsaved changes</v-card-title>
        <v-card-text>
          Your changes are autosaved as a draft on this device, but they won't apply
          to new contracts until you save the template.
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" @click="cancelLeave">
            Keep editing
          </v-btn>
          <v-spacer/>
          <v-btn variant="text" @click="confirmLeave(false)">
            Leave
          </v-btn>
          <v-btn color="primary" variant="tonal" @click="confirmLeave(true)">
            Save & leave
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="shortcutsDialog" max-width="420">
      <v-card>
        <v-card-title>Keyboard shortcuts</v-card-title>
        <v-card-text>
          <div v-for="shortcut in editorShortcuts" :key="shortcut.action" class="flex items-center justify-between py-2">
            <span class="text-medium-emphasis">{{ shortcut.action }}</span>
            <span>
              <kbd v-for="key in shortcut.keys" :key="key" class="fw-kbd">{{ key }}</kbd>
            </span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn variant="text" @click="shortcutsDialog = false">
            {{ $t('okay') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="undoSnackbar" :timeout="6000" location="bottom">
      {{ lastDeletedFields.length === 1 ? 'Field removed' : `${lastDeletedFields.length} fields removed` }}
      <template v-slot:actions>
        <v-btn color="primary" variant="text" @click="undoDelete">
          Undo
        </v-btn>
      </template>
    </v-snackbar>

    <v-dialog v-model="validationDialog" max-width="460">
      <v-card>
        <v-card-title>Check your template</v-card-title>
        <v-card-text>
          <div v-for="warning in validationWarnings" :key="warning" class="flex items-start gap-2 py-1">
            <v-icon class="mt-1" color="warning" size="small">mdi-alert-outline</v-icon>
            <span>{{ warning }}</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn variant="text" @click="validationDialog = false">
            Keep editing
          </v-btn>
          <v-btn color="primary" variant="tonal" @click="saveDespiteWarnings">
            Save anyway
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-menu
      v-model="contextMenu.open"
      :target="[contextMenu.x, contextMenu.y]"
      location="bottom start"
    >
      <v-list density="compact">
        <v-list-item @click="contextMenuDuplicate">
          <template v-slot:prepend>
            <v-icon size="small">mdi-content-copy</v-icon>
          </template>
          <v-list-item-title>Duplicate</v-list-item-title>
        </v-list-item>
        <v-list-item @click="contextMenuSettings">
          <template v-slot:prepend>
            <v-icon size="small">mdi-tune</v-icon>
          </template>
          <v-list-item-title>Field settings</v-list-item-title>
        </v-list-item>
        <v-divider/>
        <v-list-item @click="contextMenuDelete">
          <template v-slot:prepend>
            <v-icon color="error" size="small">mdi-delete-outline</v-icon>
          </template>
          <v-list-item-title class="text-error">Delete</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

  </div>
</template>

<script lang="ts" setup>
import {computed, onBeforeUnmount, onMounted, ref, type ShallowRef, useTemplateRef, watch} from 'vue';
import TemplateRightSidebarV2 from '@/views/documentsV2/TemplateRightSidebarV2.vue';
import LeftSidebar from '@/components/dashboard/documents/LeftSidebar.vue';
import TopToolbar from '@/components/dashboard/documents/TopToolbar.vue';
import RightSideColumn from '@/components/dashboard/documents/RightSideColumn.vue';
import FieldDragHandles from '@/components/dashboard/documents/FieldDragHandles.vue';
import DocumentStage from '@/components/documentsV2/DocumentStage.vue';
import {dragElement, round} from '@/helpers';
import type {ESignerFieldData, ESignTool} from '@/types/esign';
import {useEsignFieldRendering} from '@/composables/documentsV2/useEsignFieldRendering';
import {useCompany} from '@/composables/useCompany';
import {useESign} from '@/composables/useESign';
import {useAppSettings} from '@/composables/useAppSettings';
import {useTemplate} from '@/composables/useTemplate';
import {onBeforeRouteLeave, useRoute} from 'vue-router';
import {push} from 'notivue'
import {d} from '@/utils/consoleHelper.ts';
import {pageNumberForY, relativeYForY, stackedCanvasBottom} from '@/utils/esignPageMath.ts';
import {type SnapGuide, snapRect} from '@/utils/esignSnapMath';
import {
  clearTemplateDraft,
  loadTemplateDraft,
  saveTemplateDraft,
  type TemplateDraft,
} from '@/composables/documentsV2/useTemplateDraft';
import {validateTemplateFields} from '@/utils/esignTemplateValidation';

export interface documentImagesInterface {
  position: number;
  path: string;
}

interface StandardDocCoords {
  top: number;
  left: number;
  right: number;
  bottom: number;
  height: number;
  width: number;
  x: number;
  y: number;
}

interface CoordinateData {
  x: number;
  y: number;
  page: number;
  pageId: string | null;
  pageTop: number;
  clientY: number;
  clientX: number;
  item_x: number;
  item_y: number;
  item_height: number;
  topBarHeight: number;
}

const route = useRoute();
const {company} = useCompany();
const {getTemplate, updateTemplate} = useESign();
const {toggleDebugDrawer, debugDrawerOpen} = useAppSettings();
const {
  documentsLoading,
  selectedFieldV2,
  allTools,
  template,
  selectedFieldV3Coordinates,
  updateSelectedField,
  templateStageData,
  updateStageData,
  setStageData,
  removeField,
  templateDocumentImages,
  documentImages,
  setDocumentImages,
  addToStageData,
  makeFieldId,
  removeAllStageData,
  setSingleTemplate,
  setToolsActive,
  currentSigner,
  documentFileName,
} = useTemplate();


const content_main = useTemplateRef('content_main') as ShallowRef<HTMLElement>;
const dataEditor = useTemplateRef('dataEditor') as ShallowRef<HTMLElement>;

const documentUuid = route.params.uuid as string;
const standardDoc = ref<HTMLElement | null>(null);
const standardDocCoords = ref<StandardDocCoords | null>(null);
const pageMarginY = 32;
const contentMainWidth = ref(612);
const contentMainHeight = ref(825);
const contentLeftSidebarWidth = ref(300);
const mainContentStartingXPosition = ref(260);
const scrollTop = ref(0);
const originalContentMainWidth = ref(612);
const originalContentMainHeight = ref(792);
const localDocumentImages = ref<documentImagesInterface[]>([]);
const currentWorkingDataElement = ref<ESignerFieldData | null>(null);
const dragDraft = ref<ESignerFieldData | null>(null);
const elementOffsetX = ref(0);
const elementOffsetY = ref(0);
const dragHandlesOn = ref(false);

/**
 * ---- Snap-to-align ----
 *
 * While dragging or placing, the moving field's edges/centers snap to other
 * fields, the page edges, and the page's horizontal center; guide lines
 * render in the stage. Hold Alt to bypass for pixel-precise placement.
 */
const activeSnapGuides = ref<SnapGuide[]>([]);

const applySnapToDraft = (draft: ESignerFieldData, disableSnap: boolean): void => {
  if (disableSnap) {
    activeSnapGuides.value = [];
    return;
  }

  const others = templateStageData.value.filter((f) => f.id !== draft.id && f.status !== 'droppable');
  const draftPage = determinePageNumber(draft.y);
  const rectOf = (f: ESignerFieldData) => ({x: f.x, y: f.y, w: f.w, h: f.h});

  const result = snapRect(rectOf(draft), {
    xTargetRects: others.map(rectOf),
    yTargetRects: others
      .filter((f) => (f.page || determinePageNumber(f.y)) === draftPage)
      .map(rectOf),
    pageWidth: docImage.value.width,
  });

  draft.x = result.x;
  draft.y = result.y;
  activeSnapGuides.value = result.guides;
};

const clearSnapGuides = (): void => {
  activeSnapGuides.value = [];
};

const stageDataForRender = computed(() => {
  const draft = dragDraft.value;
  const draftId = draft?.id;

  if (!draft || !draftId) {
    return templateStageData.value;
  }

  // Only depend on the draft's identity, not its coordinates, so we don't rebuild this list on every drag move.
  return templateStageData.value.map((f) => (f.id === draftId ? draft : f));
});


const coordinateData = ref<CoordinateData>({
  x: 0,
  y: 0,
  page: 1,
  pageId: null,
  pageTop: 0,
  clientY: 0,
  clientX: 0,
  item_x: 0,
  item_y: 0,
  item_height: 0,
  topBarHeight: 42,
})

const removeAllFieldsDialog = ref(false);
const editorText = ref('');
const searchTool = ref('');
const currentWorkingFieldId = ref<string | null>(null);
const currentWorkingTarget = ref('');

/**
 * ---- Dirty tracking + draft autosave ----
 *
 * Comparisons use a normalized projection that drops the display-scaled
 * coordinates (x/y/w/h/ratios) and transient state, keeping only the
 * resolution-independent `original*` values and field semantics. This keeps
 * window resizes (which rewrite x/y via adjustDataCoords) from reading as
 * unsaved changes. Drafts store the FULL field data so restore can re-derive
 * display coordinates for the current viewport.
 */
const baselineNormalized = ref<string | null>(null);
const isDirty = ref(false);
const draftSavedAt = ref<Date | null>(null);
const restorableDraft = ref<TemplateDraft | null>(null);
const leaveGuardDialog = ref(false);
const shortcutsDialog = ref(false);
const undoSnackbar = ref(false);
const lastDeletedFields = ref<ESignerFieldData[]>([]);
const pendingLeave = ref<((allow?: boolean) => void) | null>(null);

let autosaveInterval: ReturnType<typeof setInterval> | null = null;
let lastDraftSnapshot: string | null = null;

const normalizeFieldsForComparison = (fields: ESignerFieldData[]): string => {
  return JSON.stringify(fields.map((field) => {
    const {x, y, w, h, xRatio, yRatio, relativeY, status, ...rest} = field as Record<string, unknown>;
    return rest;
  }));
};

const currentNormalized = () => normalizeFieldsForComparison(templateStageData.value);

const refreshDirtyState = (): boolean => {
  if (baselineNormalized.value === null) {
    return false;
  }
  isDirty.value = currentNormalized() !== baselineNormalized.value;
  return isDirty.value;
};

const writeDraftIfChanged = () => {
  if (!refreshDirtyState()) {
    return;
  }
  const snapshot = JSON.stringify(templateStageData.value);
  if (snapshot !== lastDraftSnapshot) {
    saveTemplateDraft(documentUuid, snapshot);
    lastDraftSnapshot = snapshot;
    draftSavedAt.value = new Date();
  }
};

const captureBaseline = () => {
  baselineNormalized.value = currentNormalized();
  isDirty.value = false;

  const draft = loadTemplateDraft(documentUuid);
  if (!draft) {
    return;
  }

  try {
    const draftFields = JSON.parse(draft.actions) as ESignerFieldData[];
    if (normalizeFieldsForComparison(draftFields) !== baselineNormalized.value) {
      restorableDraft.value = draft;
    } else {
      clearTemplateDraft(documentUuid);
    }
  } catch {
    clearTemplateDraft(documentUuid);
  }
};

const restorableDraftLabel = computed(() => {
  if (!restorableDraft.value) {
    return '';
  }
  const saved = new Date(restorableDraft.value.savedAt);
  return saved.toLocaleString(undefined, {month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'});
});

const restoreDraft = () => {
  if (!restorableDraft.value) {
    return;
  }
  try {
    const draftFields = (JSON.parse(restorableDraft.value.actions) as ESignerFieldData[])
      .map((field) => ({...field, status: 'dropped'}));
    setStageData(draftFields);
    adjustDataCoords();
    refreshDirtyState();
    push.success({message: 'Draft restored'});
  } catch {
    push.error({message: 'Could not restore the draft'});
    clearTemplateDraft(documentUuid);
  }
  restorableDraft.value = null;
};

const discardDraft = () => {
  clearTemplateDraft(documentUuid);
  restorableDraft.value = null;
  lastDraftSnapshot = null;
};

const undoDelete = () => {
  lastDeletedFields.value.forEach((field) => addToStageData({...field}));
  lastDeletedFields.value = [];
  undoSnackbar.value = false;
  push.success({message: 'Fields restored'});
};

const editorShortcuts = [
  {keys: ['↑', '↓', '←', '→'], action: 'Nudge selected field 1px'},
  {keys: ['Shift', 'Arrows'], action: 'Nudge selected field 10px'},
  {keys: ['Alt', 'Drag'], action: 'Disable snapping while dragging'},
  {keys: ['Delete'], action: 'Remove selected field'},
  {keys: ['Esc'], action: 'Deselect field'},
  {keys: ['⌘', 'C'], action: 'Duplicate selected field'},
  {keys: ['⌘', 'S'], action: 'Save template'},
];

const confirmLeave = (shouldSave: boolean) => {
  leaveGuardDialog.value = false;
  const proceed = pendingLeave.value;
  pendingLeave.value = null;

  if (!proceed) {
    return;
  }

  if (shouldSave) {
    saveTemplate().then(() => proceed()).catch(() => {
    });
    return;
  }

  writeDraftIfChanged();
  proceed();
};

const cancelLeave = () => {
  leaveGuardDialog.value = false;
  const proceed = pendingLeave.value;
  pendingLeave.value = null;
  proceed?.(false);
};

onBeforeRouteLeave((_to, _from, next) => {
  if (!refreshDirtyState()) {
    next();
    return;
  }
  writeDraftIfChanged();
  pendingLeave.value = next as (allow?: boolean) => void;
  leaveGuardDialog.value = true;
});

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  if (refreshDirtyState()) {
    writeDraftIfChanged();
    event.preventDefault();
  }
};

const handleScroll = (event: Event) => {
  const target = event.target as HTMLElement;
  scrollTop.value = target.scrollTop;
};

/** The page currently in view, for highlighting the thumbnail rail. */
const currentViewPage = computed(() => {
  const viewportAnchor = scrollTop.value + (content_main.value?.clientHeight ?? 400) / 3;
  return pageNumberForY(viewportAnchor, docImage.value.height, pageMarginY, localDocumentImages.value.length);
});


const getCanvasOffset = computed(() => {
  if (!standardDocCoords.value) {
    return {
      top: 0, left: 0, right: 0, bottom: 0, height: 0
    };
  }


  const top = standardDocCoords.value.top - scrollTop.value + 20;
  const left = standardDocCoords.value.left + window.scrollX;
  const right = standardDocCoords.value.right;
  const bottom = standardDocCoords.value.bottom;
  const height = standardDocCoords.value.height;
  const page = 0;
  return {
    page: Number(page),
    top: Number(top.toFixed(3)),
    left: Number(left.toFixed(3)),
    right: Number(right.toFixed(3)),
    bottom: Number(bottom.toFixed(3)),
    height: Number(height.toFixed(3)),
  };
});

const determineRelativeY = (topPosition: number): number => {
  const pos = topPosition - getCanvasOffset.value.top;
  return relativeYForY(pos, docImage.value.height, pageMarginY, templateDocumentImages.value.length);
};


const rightSidebarOpen = computed(() => {
  return selectedFieldV2.value?.id;
});

const LeftAndRightDocumentMargin = computed((): number => {
  return (contentMainWidth.value - pageViewRectangle.value) / 2; // usually 20 px
});

const widthChange = computed((): number => {
  return (pageViewRectangle.value - originalContentMainWidth.value) / originalContentMainWidth.value;
});

const {
  tippyContent,
  elementFillColor,
  elementClassObject,
  determineYTextOffset,
  determineDisplayFontSize,
  determineTextLines,
} = useEsignFieldRendering(widthChange);

const documentStartingXPosition = computed((): number => {
  return LeftAndRightDocumentMargin.value + mainContentStartingXPosition.value; // usually 260 px
});

const objectRatioHeightRev = (scaled: number): number => {
  const newHeight = docImage.value.height;
  const savedHeight = originalContentMainHeight.value;
  const tempScale = scaled / newHeight;
  return round(tempScale * savedHeight, 5);
};

const objectRatioWidthRev = (scaled: number): number => {
  const newWidth = docImage.value.width;
  const savedWidth = originalContentMainWidth.value;
  const tempScale = scaled / newWidth;
  return round(tempScale * savedWidth, 5);
};

/**
 * Keep the persisting "original*" coordinates in sync with the current scaled values.
 * `adjustDataCoords()` recomputes x/y/w/h from `original*`, so if `original*` isn't updated
 * after moves/resizes, a window resize will snap fields back to older positions.
 */
const syncOriginalCoordsFromCurrent = (field: ESignerFieldData): ESignerFieldData => {
  const page = field.page || determinePageNumber(field.y);
  const adjust = pageHeightAdjust(page);
  const relativeY = determineRelativeY(field.y + getCanvasOffset.value.top);

  return {
    ...field,
    page,
    relativeY,
    originalX: objectRatioWidthRev(field.x),
    originalY: objectRatioHeightRev(field.y - adjust),
    originalRelativeY: objectRatioHeightRev(relativeY),
    originalWidth: objectRatioWidthRev(field.w),
    originalHeight: objectRatioHeightRev(field.h),
  };
};

const pageHeightAdjust = (page: number): number => {
  return ((pageMarginY * (page - 1)) * 100000) / 100000;
};

const handleWindowResize = () => {
  contentMainWidth.value = content_main.value.clientWidth;
  contentMainHeight.value = contentMainWidth.value / docImage.value.ratio * localDocumentImages.value.length;
};


const getSelectedElements = () => {
  return Array.from(document.querySelectorAll('.field-selected'));
};


const removeSelectedElements = () => {
  const selected = getSelectedElements();

  selected.forEach((f) => {
    f.setAttribute('stroke', '#CAEFF');
    f.setAttribute('stroke-width', '1');
    f.classList.remove('field-selected');
  });

  dragHandlesOn.value = false;
};

const handleKeyup = (evt: KeyboardEvent) => {
  evt.preventDefault();
  const target = evt.target as HTMLElement;
  const key = evt.key.toLowerCase();
  const isInputFocused = ['input', 'textarea'].includes(target.localName);

  if (isInputFocused) return;

  switch (key) {
    /** DELETE / BACKSPACE - Delete Selected Fields */
    case 'backspace':
    case 'delete':
      removeSelectedItems()
      break;

    case 'c':
      if (evt.ctrlKey || evt.metaKey) {
        evt.preventDefault();
        copySelectedElement();
      }
      break;

    /** ESCAPE - Deselect Current Field */
    case 'escape':
      removeSelectedElements();
      currentWorkingDataElement.value = null;
      updateSelectedField(null);
      clearSnapGuides();
      break;
  }

};

const removeSelectedItems = () => {
  const dropItems = getSelectedElements();
  dragHandlesOn.value = false;

  if (dropItems.length === 0) {
    dragHandlesOn.value = false;
    return;
  }

  const elementIdsToRemove = dropItems
    .map((data) => data.parentElement?.id)
    .filter((id): id is string => !!id);

  // Instant visual feedback, then let Vue remove from DOM.
  dropItems.forEach((data) => data.setAttribute('style', 'display:none;'));

  // Actually remove the fields (don't leave placeholder rows in template data),
  // keeping copies so the removal can be undone from the snackbar.
  const removedFields: ESignerFieldData[] = [];
  elementIdsToRemove.forEach((id) => {
    const field = templateStageData.value.find((f) => f.id === id);
    if (field) {
      removedFields.push({...field});
      removeField(field);
    }
  });

  removeSelectedElements();
  currentWorkingDataElement.value = null;
  updateSelectedField(null);

  if (removedFields.length > 0) {
    lastDeletedFields.value = removedFields;
    undoSnackbar.value = true;
  }

  dragHandlesOn.value = false;
};

const moveSelectedElement = (key: string, evt: KeyboardEvent) => {
  evt.preventDefault();
  const selected = getSelectedElements();

  const moveStep = evt.shiftKey ? 10 : 1; // Shift + Arrow Key moves by 10 px, otherwise 1 px


  if (selected.length > 0) {
    const elementId = selected[0].parentElement?.id;
    const dataIndex = templateStageData.value.findIndex((data) => data.id === elementId);

    if (dataIndex !== -1) {
      const data = templateStageData.value[dataIndex];
      let newX = data.x;
      let newY = data.y;

      switch (key) {
        case 'arrowup':
          newY -= moveStep;
          break;
        case 'arrowdown':
          newY += moveStep;
          break;
        case 'arrowleft':
          newX -= moveStep;
          break;
        case 'arrowright':
          newX += moveStep;
          break;
      }

      newX = verifyElementXCoords(newX);
      newY = verifyElementYCoords(newY);
      const moved = syncOriginalCoordsFromCurrent({
        ...data,
        x: newX,
        y: newY,
        page: determinePageNumber(newY),
      });

      updateStageData({
        data: moved,
        index: dataIndex
      });

      dragDraft.value = {...moved};
      currentWorkingDataElement.value = dragDraft.value;
    }
  }
};

const firstStagedData = computed(() => {
  return templateStageData.value[0];
});

const customerDefaultProperties = computed(() => {
  return company.value?.default_properties?.filter((f) => ['customer', 'order'].includes(f.owner_type));
});

const filteredTools = computed((): ESignTool[] => {
  // Filter out disabled tools
  const tools = allTools.value.filter((t: ESignTool) => !t.disabled && t.name.toLowerCase().includes(searchTool.value.toLowerCase()));

  // Map to a specific structure
  const defaultProperties = (customerDefaultProperties.value ?? []).map((dp) => ({
    id: `custom-field-${dp.uuid}`,
    name: dp.name,
    type: 'field',
    category: 'Custom Fields',
    icon: 'mdi-focus-field-horizontal',
    customField: true,
    w: (dp.name.length * 9) + 10,
    h: 20,
  }));

  return [...tools, ...defaultProperties];
});

const WIDTH_WHEN_TOOLING = 928;

/**
 * Adjusts the width of the tool based on the current image width
 * NOTE: I set the tool width based on my screen size at the time of development.
 * So, this should be adjusted to the width of the screen when the tool was placed.
 */
const toolWidthAdjusted = (width: number): number => {
  const currentImageWidth = +docImage.value.width;
  const widthDifference = currentImageWidth - WIDTH_WHEN_TOOLING;
  const widthChangeAdjust = widthDifference === 0
    ? 0
    : widthDifference / WIDTH_WHEN_TOOLING;

  return round((width + (width * widthChangeAdjust)), 5);
};

// Convert a measured width in *current pixels* back into the baseline "tooling units"
// expected by `toolWidthAdjusted()`. This prevents double-scaling.
const toolingUnitsFromCurrentPx = (px: number): number => {
  const currentImageWidth = +docImage.value.width;
  if (!currentImageWidth || currentImageWidth <= 0) {
    return px;
  }
  return px * (WIDTH_WHEN_TOOLING / currentImageWidth);
};


/**
 * Adjusts the height of the tool based on the current adjusted width
 * NOTE: I set the tool width based on my screen size at the time of development.
 * So, this should be adjusted to the width of the screen when the tool was placed.
 */
const toolHeightAdjusted = (width: number, height: number): number => {
  return round(toolWidthAdjusted(width) * (height / width), 5);
};

const objectRatioWidth = (width: number): number => objectRatio(width, originalContentMainWidth.value, 'width');

const objectRatioHeight = (height: number): number => objectRatio(height, originalContentMainHeight.value, 'height');

const objectRatio = (value: number, reference: number, label: string): number => {
  const numericValue = Number(value);

  if (isNaN(numericValue)) {
    console.error(`Invalid ${label} value:`, value);
    return 0;
  }

  return Math.abs(parseFloat((numericValue / reference).toFixed(5)));
};

watch(template, (templateNew) => {
  originalContentMainWidth.value = templateNew?.documents?.dimensions?.width ?? 612;
  originalContentMainHeight.value = templateNew?.documents?.dimensions?.height ?? 792;
}, {once: true});


const adjustDocumentContainerHeight = () => {
  const docHeight = pageViewRectangle.value / docImage.value.ratio;

  // Deep clone each image object
  localDocumentImages.value = documentImages.value.map((img: documentImagesInterface) => ({...img}));

  localDocumentImages.value.forEach((p: documentImagesInterface, index: number) => {
    if (index === 0) return;
    p.position = (index * docHeight) + (pageMarginY * index);
  });

  adjustDataCoords();
};


watch(templateDocumentImages, (images) => {
  const docHeight = round(pageViewRectangle.value / docImage.value.ratio, 5);

  setDocumentImages(images.map((i, index) => ({
    path: i,
    position: (index * docHeight) + (pageMarginY * index),
  })));

  if (images.length > 0) {
    contentMainWidth.value = content_main.value.clientWidth;
    contentMainHeight.value = contentMainWidth.value / docImage.value.ratio * images.length;

    contentLeftSidebarWidth.value = document.getElementById('content_sidebar-left')?.clientWidth ?? 0 as unknown as number;


    adjustDocumentContainerHeight();

    // The stage data has been loaded and normalized for the current viewport;
    // this is the reference point for unsaved-change detection and drafts.
    if (baselineNormalized.value === null) {
      captureBaseline();
    }
  }
});

const scrollToPage = (pageIndex: number) => {

  const pagePosition = localDocumentImages.value[pageIndex].position;
  content_main.value.scrollTo({
    top: pagePosition,
    left: 0,
    behavior: 'smooth',
  });
};

onMounted(() => {
  getTemplate(documentUuid);

  window.addEventListener('resize', handleWindowResize, false);
  document.addEventListener('keyup', handleKeyup, false);
  document.addEventListener('keydown', handleKeydown, false);
  window.addEventListener('beforeunload', handleBeforeUnload);

  autosaveInterval = setInterval(writeDraftIfChanged, 5000);


  standardDoc.value = document.getElementById('standard-documents');

  // esign-editor-main-content
  const mainContent = document.getElementById('esign-editor-main-content');

  // how far from the left of the window
  mainContentStartingXPosition.value = mainContent?.getBoundingClientRect().left ?? 0;


  // console.dir(standardDoc.value);
  // console.dir(standardDoc.value.getBoundingClientRect());
  const standardDocCoordsRect = standardDoc.value?.getBoundingClientRect() ?? {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  };

  standardDocCoords.value = {
    top: standardDocCoordsRect.top,
    left: standardDocCoordsRect.left,
    right: standardDocCoordsRect.right,
    bottom: standardDocCoordsRect.bottom,
    height: standardDocCoordsRect.height,
    width: standardDocCoordsRect.width,
    x: standardDocCoordsRect.x,
    y: standardDocCoordsRect.y,
  };

  const draggableDebug = Array.from(document.getElementsByClassName('debug-draggable')) as HTMLElement[];
  draggableDebug.forEach((el) => {
    dragElement(el);
  });
});


const pageViewRectangle = computed(() => {
  return contentMainWidth.value - 40;
});

const templateDocumentDimensions = computed(() => {
  return template.value?.documents?.dimensions ?? {
    width: 612,
    height: 792,
  };
});

const templateDocumentDimensionRatio = computed(() => {
  const h = templateDocumentDimensions.value.height;
  const w = templateDocumentDimensions.value.width;
  return round(w / h, 5);
});

const docImage = computed(() => {
  const width = pageViewRectangle.value;
  const height = width / templateDocumentDimensionRatio.value;
  const ratio = width / height;
  return {
    width,
    height,
    ratio,
  };
});


const adjustDataCoords = () => {
  const next = templateStageData.value.map((data: ESignerFieldData) => {
    const adjust = pageHeightAdjust(data.page || 1);

    const xRatio = objectRatioWidth(data.originalX);
    const yRatio = objectRatioHeight(data.originalY);

    const x = (docImage.value.width * xRatio) < 0 ? 0 : round(docImage.value.width * xRatio, 5);
    const y = Math.round(((docImage.value.height * yRatio) + adjust) * 100000) / 100000;
    const w = round(docImage.value.width * objectRatioWidth(data.originalWidth), 5);
    const h = round(docImage.value.height * objectRatioHeight(data.originalHeight), 5);
    const page = determinePageNumber(y);

    return {
      ...data,
      xRatio,
      yRatio,
      x,
      y,
      w,
      h,
      page,
    };
  });

  setStageData(next);

  // Keep selected + working field references in sync after replacing array items.
  const selectedId = selectedFieldV2.value?.id;
  if (selectedId) {
    updateSelectedField(next.find((f) => f.id === selectedId) ?? null);
  }

  const workingId = dragDraft.value?.id ?? currentWorkingDataElement.value?.id;
  if (!workingId) {
    return;
  }

  const updated = next.find((f) => f.id === workingId) ?? null;

  // If we have an active draft, keep using the draft (not a Vuex reference)
  // but refresh it from the latest computed coords.
  if (dragDraft.value && dragDraft.value.id === workingId) {
    dragDraft.value = updated ? {...updated} : null;
    currentWorkingDataElement.value = dragDraft.value;
    return;
  }

  currentWorkingDataElement.value = updated;
};

const verifyElementXCoords = (x: number) => {
  // e.clientX - elementOffsetX.value - documentStartingXPosition.value
  const element = currentWorkingDataElement.value;

  if (!element) {
    return x;
  }

  const documentWidth = docImage.value.width;

  // 🛑 Prevent movement outside the left side
  if (x < 0) {
    return 0; // Prevent going below the first page
  }

  // 🛑 Prevent movement outside the right side
  if (x + element.w > documentWidth) {
    return documentWidth - element.w; // Prevent going below the first page
  }

  // ✅ Allow movement across pages
  return x;

}

const verifyElementYCoords = (finalY: number) => {
  if (finalY < 0) return 0; // Prevent dragging above the first page

  const element = currentWorkingDataElement.value;

  if (!element) {
    return finalY;
  }

  const documentHeight = docImage.value.height;
  const totalPages = localDocumentImages.value.length;
  const currentPage = determinePageNumber(finalY);

  const pageOffset = (currentPage - 1) * (documentHeight + pageMarginY);
  const pageEndY = pageOffset + documentHeight;
  const elementBottom = finalY + element.h;

  // 🛑 Prevent stopping in the page gap
  if ((finalY + element.h) >= pageEndY && finalY < pageEndY + pageMarginY) {
    return pageEndY - element.h; // Snap to the start of the next page
  }

  // ✅ Fix last page issue: Adjust the final Y limit
  const lastPageHeight = (documentHeight * totalPages) + ((totalPages - 1) * pageMarginY);

  // 🛑 Prevent movement below the last page
  if (currentPage === totalPages && elementBottom > lastPageHeight) {
    return lastPageHeight - element.h; // Prevent going below the last page
  }

  // ✅ Handle transitions smoothly (Optional Optimization)
  if (finalY < pageOffset && elementBottom > pageEndY - element.h) {
    return pageOffset; // Snap to the top of the page when transitioning
  }

  // ✅ Allow movement across pages
  return finalY;
};


const dropListener = (e: MouseEvent) => {
  d.dir('drop');
  e.preventDefault();

  if (!dragDraft.value) {
    return;
  }

  try {
    // Persist against the actual dropped coordinates (not selected coords).
    // This prevents window resize from snapping back to stale `original*` values.
    const page = dragDraft.value.page || determinePageNumber(dragDraft.value.y);
    const adjust = pageHeightAdjust(page);
    const theDeterminedY = determineRelativeY(dragDraft.value.y + getCanvasOffset.value.top);

    d.log('theDeterminedY: ' + theDeterminedY);
    dragDraft.value.page = page;
    dragDraft.value.relativeY = theDeterminedY;
    dragDraft.value.originalX = objectRatioWidthRev(dragDraft.value.x);
    dragDraft.value.originalY = objectRatioHeightRev(dragDraft.value.y - adjust);
    dragDraft.value.originalRelativeY = objectRatioHeightRev(theDeterminedY);
    dragDraft.value.originalWidth = objectRatioWidthRev(dragDraft.value.w);
    dragDraft.value.originalHeight = objectRatioHeightRev(dragDraft.value.h);
  } catch (error) {
    d.error(error);
  }

  elementOffsetX.value = 0;
  elementOffsetY.value = 0;

  // Persist the draft back to Vuex once, on the drop end.
  const draftId = dragDraft.value.id;
  const index = templateStageData.value.findIndex((f) => f.id === draftId);
  if (index !== -1) {
    updateStageData({
      data: {...dragDraft.value},
      index,
    });
  }

  // Refresh the draft from Vuex so we keep selection/handles without holding a Vuex reference.
  const updated = templateStageData.value.find((f) => f.id === draftId);
  dragDraft.value = updated ? {...updated} : null;
  currentWorkingDataElement.value = dragDraft.value;

  clearSnapGuides();
};


const adjustOriginalCoords = () => {
  templateStageData.value.forEach((data: ESignerFieldData, dataIndex: number) => {
    const adjust = pageHeightAdjust(data?.page || 1);

    templateStageData.value[dataIndex].originalX = objectRatioWidthRev(templateStageData.value[dataIndex].x);
    templateStageData.value[dataIndex].originalY = objectRatioHeightRev(templateStageData.value[dataIndex].y - adjust);

    templateStageData.value[dataIndex].originalWidth = objectRatioWidthRev(templateStageData.value[dataIndex].w);
    templateStageData.value[dataIndex].originalHeight = objectRatioHeightRev(templateStageData.value[dataIndex].h);

    // updateStageData({
    //   data: {
    //     ...templateStageData.value[dataIndex],
    //     originalX: objectRatioWidthRev(templateStageData.value[dataIndex].x),
    //     originalY: objectRatioHeightRev(templateStageData.value[dataIndex].y - adjust),
    //   },
    //   index: dataIndex,
    // });
  });
};

const saveTemplate = async () => {
  adjustOriginalCoords();

  if (!template.value) {
    return;
  }

  const actions = JSON.stringify(templateStageData.value);

  await updateTemplate({
    actions: btoa(encodeURI(actions)),
    template: template.value,
  });

  // Save succeeded: reset the dirty baseline and drop the local draft.
  baselineNormalized.value = currentNormalized();
  isDirty.value = false;
  lastDraftSnapshot = null;
  draftSavedAt.value = null;
  clearTemplateDraft(documentUuid);
};

const validationDialog = ref(false);
const validationWarnings = ref<string[]>([]);

/**
 * Save entry point for UI triggers (toolbar, Cmd+S). Runs the pre-save sanity
 * checks first; warnings open a confirm dialog instead of saving silently.
 * Failures already surface via the store's error toast.
 */
const saveTemplateSafely = () => {
  const warnings = validateTemplateFields(templateStageData.value);
  if (warnings.length > 0) {
    validationWarnings.value = warnings;
    validationDialog.value = true;
    return;
  }
  saveTemplate().catch(() => {
  });
};

const saveDespiteWarnings = () => {
  validationDialog.value = false;
  saveTemplate().catch(() => {
  });
};


const handleKeydown = (evt: KeyboardEvent) => {
  const key = evt.key.toLowerCase();

  /**
   * If the user is pressing the command and s key, then save the document
   * Note: this needs to be on keydown and not keyup
   */
  if (evt.metaKey && key === 's') {
    evt.preventDefault();
    if (documentsLoading.value.saving === false) {
      saveTemplateSafely();
    }
  }
  /**
   * If the user is pressing the command and c key, then copy the selected element
   */
  if (evt.metaKey && key === 'c') {
    evt.preventDefault();
    copySelectedElement();
  }

  /** ARROW KEYS - Move Selected Field */
  if (key === 'arrowup' || key === 'arrowdown' || key === 'arrowleft' || key === 'arrowright') {
    moveSelectedElement(key, evt);
  }

}

const determinePageNumber = (topPosition: number): number => {
  return pageNumberForY(topPosition, docImage.value.height, pageMarginY, templateDocumentImages.value.length);
};

const duplicateField = (source: ESignerFieldData) => {
  const newElement = JSON.parse(JSON.stringify(source)) as ESignerFieldData;
  newElement.id = `${newElement.type}-${makeFieldId()}`;
  newElement.x = Math.max(0, Math.min(source.x + 10, docImage.value.width - source.w));
  newElement.y = source.y + 10;

  // Re-sync `original*` to the offset position; otherwise a window resize
  // (adjustDataCoords) snaps the copy back on top of the source field.
  addToStageData(syncOriginalCoordsFromCurrent(newElement));

  push.success({
    message: 'Copied',
  });
};

const copySelectedElement = () => {
  const selectedElement = getSelectedElements();

  if (selectedElement.length > 0) {
    const selectedElementData = templateStageData.value.find((stageData: ESignerFieldData) => stageData.id === selectedElement[0]?.parentElement?.id);

    if (selectedElementData) {
      removeSelectedElements();
      duplicateField(selectedElementData);
    }
  }
};

const deleteFieldWithUndo = (field: ESignerFieldData) => {
  lastDeletedFields.value = [{...field}];
  removeField(field);
  removeSelectedElements();

  if (currentWorkingDataElement.value?.id === field.id) {
    currentWorkingDataElement.value = null;
    dragDraft.value = null;
  }
  if (selectedFieldV2.value?.id === field.id) {
    updateSelectedField(null);
  }

  undoSnackbar.value = true;
};

const contextMenu = ref<{ open: boolean; x: number; y: number; field: ESignerFieldData | null }>({
  open: false,
  x: 0,
  y: 0,
  field: null,
});

const onStageContextMenu = (e: MouseEvent) => {
  const target = e.target as HTMLElement;
  const fieldId = target.parentElement?.id;
  const field = fieldId ? templateStageData.value.find((f) => f.id === fieldId) : undefined;

  if (!field) {
    return;
  }

  e.preventDefault();
  contextMenu.value = {open: true, x: e.clientX, y: e.clientY, field: {...field}};
};

const contextMenuDuplicate = () => {
  if (contextMenu.value.field) {
    duplicateField(contextMenu.value.field);
  }
  contextMenu.value.open = false;
};

const contextMenuDelete = () => {
  if (contextMenu.value.field) {
    deleteFieldWithUndo(contextMenu.value.field);
  }
  contextMenu.value.open = false;
};

const contextMenuSettings = () => {
  const field = contextMenu.value.field
    ? templateStageData.value.find((f) => f.id === contextMenu.value.field?.id)
    : undefined;
  if (field) {
    signerElementClicked(field);
  }
  contextMenu.value.open = false;
};

const checkCoords = (e: MouseEvent) => {
  dragHandlesOn.value = !!(e.target as HTMLElement).hasAttribute('resizable');
};


const formatJson = (json: any) => {
  let html = '<span class="fw-json-start-bracket">{</span>';
  if (json) {

    for (const key in json) {

      if (json.hasOwnProperty(key)) {
        html += `<div class="fw-json-item"><span class="fw-json-key">${key}</span>: <span class="fw-json-value">${json[key]}</span></div>`;
      }
    }
  }
  html += '<span class="fw-json-end-bracket">}</span>';
  return html;
};


const countCheckboxes = () => {
  let count = 1;
  templateStageData.value.forEach((data: ESignerFieldData) => {
    if (data.type === 'checkbox') {
      count++;
    }
  });
  return count;
};

const removeAllFields = () => {
  dragHandlesOn.value = false;
  removeSelectedElements();
  lastDeletedFields.value = templateStageData.value.map((field) => ({...field}));
  removeAllStageData();
  updateSelectedField(null);
  removeAllFieldsDialog.value = false;

  if (lastDeletedFields.value.length > 0) {
    undoSnackbar.value = true;
  }
};

const signerElementClicked = (data: ESignerFieldData) => {
  // Keep a local draft so drag/resize don't directly mutate Vuex-backed objects.
  dragDraft.value = {...data};
  currentWorkingDataElement.value = dragDraft.value;
  dragHandlesOn.value = dragDraft.value.resizable;

  const storeField = templateStageData.value.find((f) => f.id === dragDraft.value?.id);
  updateSelectedField(storeField ?? data);
};

const onDocViewLoad = (_e: Event) => {
};

const onImgLoad = (_payload: Event) => {
};

const removeDraggingEventListeners = (elementId: string) => {
  const signingElement = document.getElementById(elementId);
  const mainContent = document.querySelector('.content_main');

  if (signingElement) {
    signingElement.removeEventListener('dragstart', dragStartListener);
  }
  if (mainContent) {
    mainContent.removeEventListener('dragover', dragOverListener as EventListener);
  }
  document.body.removeEventListener('drop', dropListener);
};

const dragStartListener = (e: DragEvent) => {
  const signingElement = document.getElementById(currentWorkingFieldId.value ?? '');
  e.dataTransfer?.setData('text/plain', 'element1');

  // Use the actual field rectangle for drag offset calculations.
  // The <g> can contain editor-only labels (e.g., captions) that change its bounding box,
  // which causes an initial "jump" when starting a drag.
  const rectEl = signingElement?.querySelector?.('.field-rectangle') as HTMLElement | null;
  const boundingRect = (rectEl ?? signingElement)?.getBoundingClientRect();

  if (!boundingRect) {
    return;
  }

  elementOffsetX.value = e.clientX - boundingRect.left;
  elementOffsetY.value = e.clientY - boundingRect.top;

  // Ensure we have a local draft to drag (don't mutate Vuex-backed data directly).
  if (!dragDraft.value && currentWorkingFieldId.value) {
    const field = templateStageData.value.find((f) => f.id === currentWorkingFieldId.value);
    if (field) {
      dragDraft.value = {...field};
      currentWorkingDataElement.value = dragDraft.value;
    }
  }
};

const dragOverListener = (e: MouseEvent) => {

  if (!dragDraft.value) {
    return;
  }

  dragDraft.value.x = verifyElementXCoords(e.clientX - elementOffsetX.value - documentStartingXPosition.value);
  dragDraft.value.y = verifyElementYCoords(e.clientY - elementOffsetY.value - getCanvasOffset.value.top);
  applySnapToDraft(dragDraft.value, e.altKey);
  dragDraft.value.page = determinePageNumber(dragDraft.value.y);

  e.preventDefault(); // Allow a drop
};

const svgClicked = (e: MouseEvent) => {
  const target = e.target as HTMLElement;

  if (!target.parentElement) {
    return;
  }

  dataEditor.value.style.display = 'none';

  if (currentWorkingTarget.value && currentWorkingTarget.value !== target.id) {
    const theId = `#${currentWorkingTarget.value}`;
    const oldTarget = document.querySelector(theId);
    if (oldTarget) {
      oldTarget.classList.remove('field-selected');
    }
  }
  currentWorkingTarget.value = target.parentElement.id;

  const hasDroppable = templateStageData.value.some((f: ESignerFieldData) => f.status === 'droppable');

  if (hasDroppable) {
    const draft = dragDraft.value;
    const next = templateStageData.value.map((f: ESignerFieldData) => {
      if (draft && f.id === draft.id) {
        // This is the "place a new field" flow. We must sync `original*` to the
        // dropped coordinates; otherwise window resize (adjustDataCoords) will
        // snap the field back to its original creation point.
        const dropped = syncOriginalCoordsFromCurrent(clampFieldWidthToPage(draft));
        return {
          ...dropped,
          status: 'dropped',
        };
      }

      return {
        ...f,
        status: 'dropped',
      };
    });
    setStageData(next);

    removeSelectedElements();
    currentWorkingDataElement.value = null;
    dragDraft.value = null;
    updateSelectedField(null);
    clearSnapGuides();
  }

  if (!target.classList.contains('fw-signing-element-rect') && target.id !== 'fw-signing-element-rect-se') {
    target.classList.remove('field-selected');

    if (currentWorkingFieldId.value) {
      removeDraggingEventListeners(currentWorkingFieldId.value);
    }
  }

  if (!target.hasAttribute('selectable')) {
    removeSelectedElements();
    currentWorkingDataElement.value = null;
    dragDraft.value = null;
    updateSelectedField(null);
  }

  target.classList.add('working');
  initElementsDrag();
};


const initElementsDrag = () => {
  const mainContent = document.querySelector('.content_main');
  const signingElement = document.getElementById(currentWorkingFieldId.value ?? '') as HTMLElement;

  if (signingElement && mainContent) {
    signingElement.addEventListener('dragstart', dragStartListener);

    mainContent.addEventListener('dragover', dragOverListener as EventListener);

    document.body.addEventListener('drop', dropListener);
  }
};


// Auto-fit field box to the current font size while editing:
// - When font size increases: grow width/height as needed so text fits.
// - When font size decreases: shrink height to fit, but do NOT shrink width (except to stay on-page).
const measureCtx = ref<CanvasRenderingContext2D | null>(null);
const getMeasureCtx = (): CanvasRenderingContext2D | null => {
  if (measureCtx.value) {
    return measureCtx.value;
  }
  if (typeof document === 'undefined') {
    return null;
  }
  const canvas = document.createElement('canvas');
  measureCtx.value = canvas.getContext('2d');
  return measureCtx.value;
};

const autoFitFieldBoxForFontChange = (field: ESignerFieldData, previousFontSize: number | null) => {
  if (!field?.id) return;
  if (!['field', 'text'].includes(field.type)) return;

  const ctx = getMeasureCtx();
  const displayFontPx = determineDisplayFontSize(field);
  const text = field.text ?? '';
  const linesRaw = text.split('\n');

  let measuredMax = 0;
  if (ctx) {
    ctx.font = `${displayFontPx}px "Lucida Console", "Andale Mono", Monaco, monospace`;
    for (const line of linesRaw) {
      measuredMax = Math.max(measuredMax, ctx.measureText(line).width);
    }
  } else {
    measuredMax = Math.max(...linesRaw.map((l) => l.length)) * displayFontPx * 0.8;
  }

  const paddingX = 8;
  const paddingY = 6;
  const minW = 50;
  const minH = 20;

  const pageMaxW = Math.max(minW, docImage.value.width - field.x - 8);
  const desiredW = Math.ceil(measuredMax + paddingX);

  const isGrowing = previousFontSize === null || (field.fontSize ?? 0) > previousFontSize;
  const nextW = isGrowing
    ? Math.min(Math.max(field.w, desiredW, minW), pageMaxW)
    : Math.min(Math.max(field.w, minW), pageMaxW); // don't shrink width on downsize

  // Height should always fit the rendered lines.
  // Note: `field` tools render single-line in the editor; `text` tools can wrap.
  const lineCount = field.type === 'text'
    ? (determineTextLines({...field, w: nextW}).length || 1)
    : 1;
  const lineHeight = Math.max(11, Math.round(displayFontPx * 1.2));
  const desiredH = Math.ceil(lineCount * lineHeight + paddingY);
  const nextH = Math.max(minH, desiredH);

  if (nextW === field.w && nextH === field.h) {
    return;
  }

  const updated = syncOriginalCoordsFromCurrent({
    ...field,
    w: nextW,
    h: nextH,
  });

  const index = templateStageData.value.findIndex((data) => data.id === field.id);
  if (index !== -1) {
    updateStageData({data: updated, index});
  }

  // Keep working references in sync so the UI updates immediately.
  if (dragDraft.value?.id === field.id) dragDraft.value = {...updated};
  if (currentWorkingDataElement.value?.id === field.id) currentWorkingDataElement.value = {...updated};
  updateSelectedField(updated);
};

watch(() => ({
    id: selectedFieldV2.value?.id ?? null,
    fontSize: selectedFieldV2.value?.fontSize ?? null,
  }), (next, prev) => {
    // Only auto-fit when the *same* field's font size changes.
    // This avoids resetting a manually resized height when re-selecting a field.
    if (!next.id || next.fontSize === null) return;
    if (!prev?.id || prev.id !== next.id) return;
    if (prev.fontSize === next.fontSize) return;

    const field = selectedFieldV2.value;
    if (!field?.id) return;
    autoFitFieldBoxForFontChange(field, prev.fontSize);
  }
);

watch(
  () => ({
    id: selectedFieldV2.value?.id ?? null,
    x: selectedFieldV2.value?.x ?? null,
    y: selectedFieldV2.value?.y ?? null,
    w: selectedFieldV2.value?.w ?? null,
    h: selectedFieldV2.value?.h ?? null,
    page: selectedFieldV2.value?.page ?? null,
  }),
  (next, prev) => {
    // Keep `original*` in sync when the sidebar edits x/y/w/h directly (via a v-model),
    // otherwise window resize (adjustDataCoords) will snap the field back.
    if (!next.id) return;
    if (!prev?.id || prev.id !== next.id) return;
    if (
      prev.x === next.x
      && prev.y === next.y
      && prev.w === next.w
      && prev.h === next.h
      && prev.page === next.page
    ) {
      return;
    }

    const field = selectedFieldV2.value;
    if (!field?.id) return;

    const synced = syncOriginalCoordsFromCurrent({...field});

    // Avoid churn if syncing doesn't change anything meaningful.
    if (
      synced.originalX === field.originalX
      && synced.originalY === field.originalY
      && synced.originalWidth === field.originalWidth
      && synced.originalHeight === field.originalHeight
      && synced.originalRelativeY === field.originalRelativeY
      && synced.page === field.page
      && synced.relativeY === field.relativeY
    ) {
      return;
    }

    const index = templateStageData.value.findIndex((data) => data.id === synced.id);
    if (index !== -1) {
      updateStageData({data: synced, index});
    }

    if (dragDraft.value?.id === synced.id) dragDraft.value = {...synced};
    if (currentWorkingDataElement.value?.id === synced.id) currentWorkingDataElement.value = {...synced};
    updateSelectedField(synced);
  }
);

watch(() => contentMainWidth.value, () => {
  dataEditor.value.style.display = 'none';
  adjustDataCoords();
  adjustDocumentContainerHeight();
});

const selectElement = (e: MouseEvent, data: ESignerFieldData) => {
  const target = e.target as HTMLElement;

  removeSelectedElements();
  dragHandlesOn.value = target.hasAttribute('resizable');
  currentWorkingFieldId.value = target.parentElement?.id ?? '';
  svgClicked(e);
  if (target.hasAttribute('selectable')) {
    target.classList.add('field-selected');
    dragDraft.value = {...data};
    currentWorkingDataElement.value = dragDraft.value;
    signerElementClicked(dragDraft.value);
  }
};

const checkDroppable = (e: MouseEvent) => {
  const target = e.target as HTMLElement;
  const targetParent = target.parentNode as HTMLElement;

  coordinateData.value = {
    ...coordinateData.value,
    pageId: targetParent.id,
    clientY: e.clientY,
    clientX: e.clientX,
  };

  if (targetParent.id.startsWith('pdf-')) {
    updateCoordinateDataForPage(targetParent, e);
  }

  // During "place a new field" mode, the UI renders via `dragDraft`, so we must
  // update the draft (not a Vuex-backed object) for the preview to follow the mouse.
  const active = dragDraft.value ?? currentWorkingDataElement.value;
  if (!active || active.status !== 'droppable') {
    return;
  }

  setToolsActive(false);

  const contentMain = document.querySelector('.content_main') as HTMLElement | null;
  if (!contentMain) {
    return;
  }

  const top = e.pageY + contentMain.scrollTop + 2; // Allows the element to follow the mouse on scroll

  if (!dragDraft.value || dragDraft.value.id !== active.id) {
    dragDraft.value = {...active};
  }

  dragDraft.value.x = e.pageX - documentStartingXPosition.value;
  dragDraft.value.y = top
    - 20 // the height of the top of the first page to the top bar height bottom
    - coordinateData.value.topBarHeight;
  applySnapToDraft(dragDraft.value, e.altKey);
  dragDraft.value.page = determinePageNumber(dragDraft.value.y);
  dragDraft.value.relativeY = determineRelativeY(dragDraft.value.y);

  const theDeterminedY = determineRelativeY(dragDraft.value.y + getCanvasOffset.value.top);
  dragDraft.value.originalRelativeY = objectRatioHeightRev(theDeterminedY);

  currentWorkingDataElement.value = dragDraft.value;
};

// 🔹 Helper function to update coordinate data for the current page
const updateCoordinateDataForPage = (targetParent: HTMLElement, e: MouseEvent) => {
  const pageNumber = +targetParent.id.replace('pdf-', '');
  coordinateData.value.page = pageNumber + 1;

  const rect = targetParent.getBoundingClientRect();
  const contentMainElement = document.querySelector('.content_main') as HTMLElement;
  const offsetParent = contentMainElement.offsetParent as HTMLElement;

  coordinateData.value.pageTop = rect.top - offsetParent.offsetTop;

  coordinateData.value.x = e.clientX - rect.left;
  coordinateData.value.y = round(
    e.clientY - coordinateData.value.topBarHeight - coordinateData.value.pageTop - 2
  );
};

const displayFontPxForTool = (toolFontSize: number, toolType: string): number => {
  // Mirror `determineDisplayFontSize` from `useEsignFieldRendering`.
  // Field/text tools get a +10px bump; signature/attachment scale directly.
  return ['signature', 'attachment'].includes(toolType)
    ? (widthChange.value * toolFontSize)
    : (widthChange.value * toolFontSize) + 10;
};

const calculateTextWidth = (text: string, fontSize: number) => {
  const ctx = getMeasureCtx();
  if (ctx) {
    ctx.font = `${fontSize}px "Lucida Console", "Andale Mono", Monaco, monospace`;
    return Math.ceil(ctx.measureText(text).width);
  }

  // Fallback: crude approximation if canvas isn't available.
  const charWidthFactor = 0.8;
  return Math.ceil(text.length * fontSize * charWidthFactor);
};

const sampleTextForTool = (tool: ESignTool): string | null => {
  return tool.sampleText ?? null;
};

const baseWidthForTool = (tool: ESignTool, fontSizePx: number, labelText: string): number => {
  // Prefer explicit tool width when provided.
  const explicit = tool.w ?? 0;

  // For some fields, the label is short but the value is long (VIN/DOB).
  const sample = sampleTextForTool(tool);
  // `calculateTextWidth()` returns current-pixel width; convert it to tooling units so
  // `toolWidthAdjusted()` doesn't double-scale the result.
  const measuredPx = calculateTextWidth(sample ?? labelText, fontSizePx);
  const measured = toolingUnitsFromCurrentPx(measuredPx);

  // Add a little padding so the text doesn't run right to the edge.
  // Keep this small to avoid fields (like VIN) feeling oversized.
  const padding = toolingUnitsFromCurrentPx(4);
  return Math.max(explicit, measured + padding, 50);
};

const clampFieldWidthToPage = (field: ESignerFieldData): ESignerFieldData => {
  if (!['field', 'text'].includes(field.type)) {
    return field;
  }

  // Respect the field's existing width but never allow it to run off the page.
  const pageMax = Math.max(50, docImage.value.width - field.x - 8);
  const nextWidth = Math.min(Math.max(field.w, 50), pageMax);

  if (nextWidth === field.w) {
    return field;
  }

  return {
    ...field,
    w: nextWidth,
    originalWidth: objectRatioWidthRev(nextWidth),
  };
};


const addField = (typeId = null) => {

  currentWorkingDataElement.value = null;

  const selectedTool = filteredTools.value.find((t) => t.id === typeId) as ESignTool;
  if (!selectedTool) return;

  const fieldId = `${selectedTool.type}-${makeFieldId()}`;
  const fontSize = selectedTool.fontSize ?? 12;
  const displayFontPx = displayFontPxForTool(fontSize, selectedTool.type);
  const label = selectedTool.name ?? '';
  const sampleText = sampleTextForTool(selectedTool) ?? '';
  // Editor-only: keep the box sized/previewed by sample text (e.g. "XXXX", "$XXXX"),
  // and keep the human label in title/toolTip so we can display it separately.
  const text = selectedTool.type === 'field'
    ? (sampleText || label)
    : '';

  // 🔹 Dynamically calculate width and height
  const baseWidth = baseWidthForTool(selectedTool, displayFontPx, label);
  const adjustedWidth = toolWidthAdjusted(baseWidth);
  const adjustedHeight = toolHeightAdjusted(baseWidth, selectedTool.h ?? 20);


  // 🔹 Determine positioning
  const startingX = 10;
  const startingY = 100 + scrollTop.value;
  const maxStartWidth = Math.max(50, docImage.value.width - startingX - 8);
  const finalWidth = Math.min(Math.max(adjustedWidth, 50), maxStartWidth);
  const originalDimensions = {
    width: objectRatioWidthRev(finalWidth),
    height: objectRatioHeightRev(adjustedHeight),
    x: objectRatioWidthRev(startingX),
    y: objectRatioHeightRev(startingY),
    relativeY: objectRatioHeightRev(startingY),
  };

  // 🔹 Create field data
  const fieldData: ESignerFieldData = {
    id: fieldId,
    page: determinePageNumber(startingY),
    x: startingX,
    y: startingY,
    relativeY: startingY,
    w: finalWidth,
    h: adjustedHeight,
    originalWidth: originalDimensions.width,
    originalHeight: originalDimensions.height,
    originalX: originalDimensions.x,
    originalY: originalDimensions.y,
    originalRelativeY: originalDimensions.relativeY,
    xRatio: null,
    text,
    title: label,
    toolTip: label,
    fontSize,
    resizable: selectedTool.resizable ?? true,
    draggable: true,
    status: 'droppable',
    required: selectedTool.required ?? true,
    readOnly: selectedTool.readOnly ?? false,
    typeId: typeId ?? selectedTool.id,
    type: selectedTool.type,
    customField: selectedTool.customField ?? false,
    version: selectedTool.version ?? 2
  };

  // 🔹 Apply conditional properties
  applyConditionalFieldProperties(fieldData, selectedTool);

  // 🔹 Add field to stage and update state
  addToStageData({...clampFieldWidthToPage(fieldData)});

  // Keep a draft so placement/dragging doesn't directly mutate Vuex state.
  dragDraft.value = {...fieldData};
  currentWorkingDataElement.value = dragDraft.value;
  currentWorkingFieldId.value = fieldId;

  const storeField = templateStageData.value.find((f) => f.id === fieldId);
  updateSelectedField(storeField ?? fieldData);

  adjustOriginalCoords();
  adjustDataCoords();
};

const applyConditionalFieldProperties = (fieldData: ESignerFieldData, tool: ESignTool) => {
  if (tool.type === 'checkbox') {
    fieldData.value = false;
    fieldData.fieldName = `Checkbox${countCheckboxes()}`;
  }
  if (tool.customField) {
    fieldData[`${currentSigner.value}_customer`] = true;
  }
  if (tool.primary) fieldData.primary_customer = true;
  if (tool.secondary) fieldData.secondary_customer = true;
};

const handleResize = (e: MouseEvent, direction: string) => {
  if (!currentWorkingDataElement.value) return;

  const startX = e.clientX;
  const startY = e.clientY;
  const startFieldX = currentWorkingDataElement.value.x;
  const startFieldY = currentWorkingDataElement.value.y;
  const startWidth = currentWorkingDataElement.value.w;
  const startHeight = currentWorkingDataElement.value.h;

  const circleTarget = e.target as Element | null;
  const handleCx = circleTarget?.getAttribute?.('cx');
  const handleCy = circleTarget?.getAttribute?.('cy');
  const cx = handleCx !== null && handleCx !== undefined ? Number(handleCx) : NaN;
  const cy = handleCy !== null && handleCy !== undefined ? Number(handleCy) : NaN;
  const isLeftHandle = !Number.isNaN(cx) && cx === 0;
  const isTopHandle = !Number.isNaN(cy) && cy === 0;

  const handleMouseMove = (moveEvent: MouseEvent) => {
    if (!currentWorkingDataElement.value) {
      return;
    }

    const deltaX = moveEvent.clientX - startX;
    const deltaY = moveEvent.clientY - startY;

    let newWidth = startWidth;
    let newHeight = startHeight;
    let newX = startFieldX;
    let newY = startFieldY;

    switch (direction) {
      case 'nwse':
        // Could be top-left OR bottom-right depending on which circle was clicked.
        if (isLeftHandle) {
          newWidth = Math.max(50, startWidth - deltaX);
          newX = startFieldX + deltaX;
        } else {
          newWidth = Math.max(50, startWidth + deltaX);
        }

        if (isTopHandle) {
          newHeight = Math.max(20, startHeight - deltaY);
          newY = startFieldY + deltaY;
        } else {
          newHeight = Math.max(20, startHeight + deltaY);
        }
        break;
      case 'nesw':
        // Could be top-right OR bottom-left depending on which circle was clicked.
        if (isLeftHandle) {
          newWidth = Math.max(50, startWidth - deltaX);
          newX = startFieldX + deltaX;
        } else {
          newWidth = Math.max(50, startWidth + deltaX);
        }

        if (isTopHandle) {
          newHeight = Math.max(20, startHeight - deltaY);
          newY = startFieldY + deltaY;
        } else {
          newHeight = Math.max(20, startHeight + deltaY);
        }
        break;
      case 'ew':
        // Left OR right edge handles both emit 'ew' - infer which one using cx.
        if (isLeftHandle) {
          newWidth = Math.max(50, startWidth - deltaX);
          newX = startFieldX + deltaX;
        } else {
          newWidth = Math.max(50, startWidth + deltaX);
        }
        break;
      case 'ns':
        // Top OR bottom edge handles both emit 'ns' - infer which one using cy.
        if (isTopHandle) {
          newHeight = Math.max(20, startHeight - deltaY);
          newY = startFieldY + deltaY;
        } else {
          newHeight = Math.max(20, startHeight + deltaY);
        }
        break;
    }

    // Clamp to page bounds so resizing can't push off-page.
    const pageWidth = docImage.value.width;

    // Clamp X + W within [0, pageWidth]
    if (newX < 0) {
      newWidth = newWidth + newX; // shrink width by overshoot
      newX = 0;
    }
    if (newX + newWidth > pageWidth) {
      newWidth = pageWidth - newX;
    }
    newWidth = Math.max(50, newWidth);

    // Clamp Y + H (prevents negative and resizing past the last page)
    if (newY < 0) {
      newHeight = newHeight + newY;
      newY = 0;
    }
    const canvasBottom = stackedCanvasBottom(docImage.value.height, pageMarginY, localDocumentImages.value.length);
    if (canvasBottom > 0 && newY + newHeight > canvasBottom) {
      newHeight = canvasBottom - newY;
    }
    newHeight = Math.max(20, newHeight);

    // Update local draft only (avoid mutating Vuex-backed objects during live drag/resize).
    currentWorkingDataElement.value.x = newX;
    currentWorkingDataElement.value.y = newY;
    currentWorkingDataElement.value.w = newWidth;
    currentWorkingDataElement.value.h = newHeight;

    // Keep persisted original coords synced so window resize doesn't revert placement.
    const synced = syncOriginalCoordsFromCurrent({
      ...currentWorkingDataElement.value,
      page: determinePageNumber(newY),
    });
    currentWorkingDataElement.value.page = synced.page;
    currentWorkingDataElement.value.relativeY = synced.relativeY;
    currentWorkingDataElement.value.originalX = synced.originalX;
    currentWorkingDataElement.value.originalY = synced.originalY;
    currentWorkingDataElement.value.originalRelativeY = synced.originalRelativeY;
    currentWorkingDataElement.value.originalWidth = synced.originalWidth;
    currentWorkingDataElement.value.originalHeight = synced.originalHeight;
    const draft = dragDraft.value;
    if (draft && draft.id === currentWorkingDataElement.value.id) {
      draft.x = newX;
      draft.y = newY;
      draft.w = newWidth;
      draft.h = newHeight;
      draft.page = synced.page;
      draft.relativeY = synced.relativeY;
      draft.originalX = synced.originalX;
      draft.originalY = synced.originalY;
      draft.originalRelativeY = synced.originalRelativeY;
      draft.originalWidth = synced.originalWidth;
      draft.originalHeight = synced.originalHeight;
    }

    // Update the stage data
    const index = templateStageData.value.findIndex(data => data.id === currentWorkingDataElement.value?.id);
    if (index !== -1) {
      updateStageData({
        data: {...currentWorkingDataElement.value},
        index
      });
    }
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
};

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleWindowResize, false);
  document.removeEventListener('keyup', handleKeyup, false);
  document.removeEventListener('keydown', handleKeydown, false);
  window.removeEventListener('beforeunload', handleBeforeUnload);

  if (autosaveInterval) {
    clearInterval(autosaveInterval);
    autosaveInterval = null;
  }
  writeDraftIfChanged();

  removeSelectedElements();
  currentWorkingDataElement.value = null;


  updateSelectedField(null);
  setSingleTemplate(null);
});
</script>

<style>
.fw-draft-banner {
  left: 50%;
  position: fixed;
  top: 56px;
  transform: translateX(-50%);
  z-index: 30;
}

.fw-kbd {
  background: rgba(var(--v-theme-on-surface), 0.08);
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
  display: inline-block;
  font-family: inherit;
  font-size: 12px;
  line-height: 1;
  margin-left: 4px;
  min-width: 22px;
  padding: 4px 6px;
  text-align: center;
}

.fw-json-item {
  color: #f8f8f2;
  margin-left: 16px;
}

.fw-json-key {
  color: #f92672;
}

.fw-json-value {
  color: #a6e22e;
}

.fw-json-start-bracket, .fw-json-end-bracket {
  color: #f92672;
}

.debug-draggable__header {
  padding: 10px;
  cursor: move;
  z-index: 10;
  background-color: #2196F3;
  color: #fff;
}

.left-sidebar-group.left-sidebar-group-active {
  background-color: rgba(var(--v-theme-on-surface), 0.08);
}

.fw-signing-element-rect {
  fill: var(--esign-primary);
  stroke: var(--esign-primary-stroke);
  stroke-width: 1;
}

.fw-signing-element-rect-secondary {
  fill: var(--esign-secondary) !important;
  stroke: var(--esign-secondary) !important;
  stroke-width: 1;
}

.field-selected {
  fill: red;
  stroke: blue;
  stroke-width: 2;
}

.resizable {
  position: relative;
}

[draggable=true] {
  -webkit-user-drag: element;
}

svg:not(:root) {
  overflow: hidden;
}

.working + text > tspan {
  color: red;
}

.site {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.site-full .content_wrap,
.site-full .footer_wrap,
.site-full .header_wrap,
.site-full .hero_wrap,
.site-full .toolbar_wrap {
  max-width: none;
}

.site_wrap {
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;
  min-height: 100vh;
}

.content_wrap {
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  min-height: 0;
  width: 100%;
}

.content_wrap,
.footer_wrap,
.header_wrap,
.hero_wrap,
.toolbar_wrap {
  margin: 0 auto;
}

.content_main {
  min-width: 0;
  flex: 1 1 0;
}

.standardDocuments {
  position: relative;
}

.site_footer, .site_header, .site_hero {
  flex: 0 0 auto;
}

.site-editor .site_content {
  flex: 1 1 0;
}

.site_content,
.site_content.content,
.site_content > .content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column !important;
  min-height: 0;
}

.site, .site_content {
  display: flex;
  width: 100%;
}

.site_content {
  flex: 1 1 auto;
  flex-direction: row;
  min-height: 0;
}

.l-flex-between {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.l-flex-between > :first-child {
  flex: 1 1 0;
  min-width: 0;
}

.drawer.down > .drawer-wrapper,
.drawer.up > .drawer-wrapper {
  width: 100%;
}


.documentPage .bar-action .btn-action {
  display: block;
  float: right;
  height: 28px;
  margin: 0;
  padding: 0;
  text-align: center;
  width: 28px;
}

.content_sidebar.sidebar-showingDrawer {
  overflow: hidden;
}

.accordion {
  background: 0 0;
  border: 0;
  cursor: pointer;
  display: block;
  font-size: inherit;
  line-height: 20px;
  padding: 6px 48px 6px 24px;
  position: relative;
  text-align: left;
  user-select: none;
  width: 100%;
}

.documentPage {
  background-color: #fff;
  border: 1px solid #ccc;
  cursor: pointer;
  display: block;
  margin: 0 auto 14px;
  position: relative;
  width: 132px;
}

.documentPage img {
  display: block;
  height: auto;
  width: 100%;
}

.documentPage .bar-action {
  background-color: #e9e9e9;
  bottom: 0;
  height: 28px;
  position: absolute;
  width: 100%;
}


.content_sidebar {
  max-width: 375px;
  min-width: 220px;
  overflow: hidden;
  width: 220px;
}

.site-editor .sidebar_header {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding: 13px 24px;
}

.form_item-iconLeft, .form_item-iconRight {
  position: relative;
}

.site-editor .toolbar_left,
.site-editor .toolbar_main,
.site-editor .toolbar_right {
  align-items: center;
  align-self: stretch;
  display: flex;
  min-height: inherit;
}

.site-editor .toolbar_left {
  max-width: 375px;
  min-width: 220px;
  padding-left: 12px;
  width: 240px;
}

.form_item-iconLeft, .form_item-iconRight {
  position: relative;
}

.form_item-iconRight .input-text {
  padding-right: 29px;
}

.tab-editor-interface_content {
  display: block;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
}

.tab-editor-interface_content textarea {
  writing-mode: horizontal-tb !important;
  text-rendering: auto;
  color: black;
  letter-spacing: normal;
  word-spacing: normal;
  text-transform: none;
  text-indent: 0;
  text-shadow: none;
  display: inline-block;
  text-align: start;
  background-color: rgb(255, 255, 255);
  flex-direction: column;
  cursor: text;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  margin: 0;
  font: 400 13px Arial;
  border-width: 1px;
  border-style: solid;
  border-color: rgb(118, 118, 118);
  border-image: initial;
  padding: 2px;
}

.tab-editor-interface_content textarea:focus {
  outline: none;
}

.tab-editor-interface_content textarea {
  width: 100%;
  height: 100%;
  resize: none;
}

text {
  display: block;
  white-space: nowrap;
}
</style>
